import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Questions from '../Questions/Questions';
import { DEMO_MODULE, DEMO_CORRECT_ANSWERS } from './mockDemoData';
import styles from './DemoExam.module.css';

/**
 * DemoExam Component
 * 
 * Wraps the Questions component to provide a demo experience without backend calls.
 * - Uses mock data from mockDemoData.js
 * - Stores answers in localStorage
 * - Calculates score client-side
 * - Shows demo banner and results
 */

const DEMO_STORAGE_KEY = 'publicq_demo_state';

const DemoExam = () => {
  const navigate = useNavigate();
  
  const [demoState, setDemoState] = useState(() => {
    const stored = localStorage.getItem(DEMO_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {
      isComplete: false,
      score: null,
      correctCount: 0,
      totalQuestions: DEMO_MODULE.questions.length,
      timeSpent: 0,
      completedAt: null
    };
  });

  const [showStartScreen, setShowStartScreen] = useState(!demoState.isComplete);
  const [demoStartTime] = useState(new Date().toISOString());

  const handleStartDemo = () => {
    // Reset demo state
    const freshState = {
      isComplete: false,
      score: null,
      correctCount: 0,
      totalQuestions: DEMO_MODULE.questions.length,
      timeSpent: 0,
      completedAt: null
    };
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(freshState));
    localStorage.removeItem('demo_user_answers'); // Clear previous answers
    setDemoState(freshState);
    setShowStartScreen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetakeDemo = () => {
    handleStartDemo();
  };

  const handleExit = useCallback(() => {
    localStorage.removeItem('demo_user_answers');
    navigate('/');
  }, [navigate]);

  // Calculate score when demo is complete
  const calculateDemoScore = useCallback(() => {
    // Get user answers from localStorage (stored by Questions component)
    const userAnswers = JSON.parse(localStorage.getItem('demo_user_answers') || '{}');
    
    let correctCount = 0;
    Object.keys(DEMO_CORRECT_ANSWERS).forEach(questionId => {
      const correctAnswers = DEMO_CORRECT_ANSWERS[questionId].sort();
      const userAnswer = (userAnswers[questionId] || []).sort();
      
      if (JSON.stringify(correctAnswers) === JSON.stringify(userAnswer)) {
        correctCount++;
      }
    });

    const score = Math.round((correctCount / DEMO_MODULE.questions.length) * 100);

    const completedState = {
      isComplete: true,
      score,
      correctCount,
      totalQuestions: DEMO_MODULE.questions.length,
      timeSpent: Math.floor((Date.now() - Date.parse(demoStartTime)) / 60000),
      completedAt: new Date().toISOString()
    };

    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(completedState));
    setDemoState(completedState);
    setShowStartScreen(false); // This will trigger showing results screen
  }, [demoStartTime]);

  if (showStartScreen) {
    return (
      <div className={styles.startScreen}>
        <div className={styles.startCard}>
          <h1 className={styles.title}>Professional Skills Demo Assessment</h1>
          
          <p className={styles.description}>
            Experience our assessment platform with this comprehensive demo. Test your logical reasoning, 
            language skills, and general knowledge through 10 carefully crafted questions.
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>
                <img src="/images/icons/test-96.png" alt="questions" style={{ width: '32px', height: '32px' }} />
              </span>
              <div>
                <strong>10 Questions</strong>
                <p>Covering logic, language, and general knowledge</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <span className={styles.featureIcon}>
                <img src="/images/icons/time-machine-96.png" alt="time" style={{ width: '32px', height: '32px' }} />
              </span>
              <div>
                <strong>20 Minutes</strong>
                <p>Time limit to complete the assessment</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <span className={styles.featureIcon}>
                <img src="/images/icons/analytics-96.png" alt="results" style={{ width: '32px', height: '32px' }} />
              </span>
              <div>
                <strong>Instant Results</strong>
                <p>Get your score immediately after submission</p>
              </div>
            </div>
            
            <div className={styles.feature}>
              <span className={styles.featureIcon}>
                <img src="/images/icons/version-94.png" alt="retake" style={{ width: '32px', height: '32px' }} />
              </span>
              <div>
                <strong>Retake Anytime</strong>
                <p>Your answers are not saved - practice freely</p>
              </div>
            </div>
          </div>

          <div className={styles.navigationTips}>
            <h3 className={styles.tipsTitle}>📍 Navigation Tips</h3>
            <ul className={styles.tipsList}>
              <li>Use the "Go to" field to jump directly to any question number</li>
              <li>
                Progress bar at the top shows color-coded status for each question:
                <div style={{ marginTop: '8px', marginLeft: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '8px', backgroundColor: '#22c55e', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '13px' }}>Green = Answered</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '8px', backgroundColor: '#f59e0b', borderRadius: '2px', cursor: 'pointer' }}></div>
                    <span style={{ fontSize: '13px' }}>Orange = Skipped (clickable to return)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '8px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
                    <span style={{ fontSize: '13px' }}>Blue = Current question</span>
                  </div>
                </div>
              </li>
              <li>When submitting, you'll see clickable badges like <span style={{ display: 'inline-block', padding: '2px 8px', backgroundColor: '#f59e0b', color: 'white', borderRadius: '4px', fontSize: '12px', fontWeight: '500', marginLeft: '4px' }}>Question 5 →</span> for any unanswered questions</li>
            </ul>
          </div>

          {demoState.isComplete && (
            <div className={styles.previousScore}>
              <h3>Your Previous Score:</h3>
              <div className={styles.scoreDisplay}>
                <span className={styles.scoreLarge}>{demoState.score}%</span>
                <span className={styles.scoreDetails}>
                  {demoState.correctCount} out of {demoState.totalQuestions} correct
                </span>
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <button 
              onClick={handleStartDemo} 
              className={styles.startButton}
            >
              {demoState.isComplete ? (
                <>
                  <img src="/images/icons/version-94.png" alt="retake" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                  Retake Assessment
                </>
              ) : (
                <>
                  <img src="/images/icons/rocket-48.png" alt="start" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                  Start Assessment
                </>
              )}
            </button>
            
            <button 
              onClick={handleExit} 
              className={styles.exitButton}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If demo is complete and user is viewing results
  if (demoState.isComplete) {
    const isPassed = (demoState.score || 0) >= DEMO_MODULE.passingScorePercentage;
    
    return (
      <div className={styles.resultsScreen}>
        <div className={styles.resultsCard}>
          <div className={styles.demoBadge}>
            <img src="/images/icons/report-card-96.png" alt="demo" style={{ width: '20px', height: '20px', marginRight: '8px' }} />
            Demo Mode
          </div>
          
          <div className={isPassed ? styles.passedIcon : styles.failedIcon}>
            {isPassed ? (
              <img src="/images/icons/certification-96.png" alt="passed" style={{ width: '64px', height: '64px' }} />
            ) : (
              <img src="/images/icons/fail-96.png" alt="failed" style={{ width: '64px', height: '64px' }} />
            )}
          </div>
          
          <h1 className={styles.resultsTitle}>
            {isPassed ? 'Congratulations!' : 'Assessment Complete'}
          </h1>
          
          <p className={styles.resultsSubtitle}>
            {isPassed 
              ? `You passed with a score of ${demoState.score}%!` 
              : `You scored ${demoState.score}%. Passing score is ${DEMO_MODULE.passingScorePercentage}%.`
            }
          </p>

          {isPassed && (
            <p className={styles.successMessage}>
              <img src="/images/icons/trophy-96.png" alt="success" style={{ width: '24px', height: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
              Excellent work! You've demonstrated great knowledge and skill. Keep up the amazing performance!
            </p>
          )}

          {!isPassed && (
            <p className={styles.encouragementMessage}>
              <img src="/images/icons/dumbbell-96.png" alt="keep going" style={{ width: '24px', height: '24px', marginRight: '8px', verticalAlign: 'middle' }} />
              Don't worry! Every attempt is a learning opportunity. Review your answers and try again - you've got this!
            </p>
          )}

          <div className={styles.resultsStats}>
            <div className={styles.statCard}>
              <span className={styles.statValue}>{demoState.score}%</span>
              <span className={styles.statLabel}>Final Score</span>
            </div>
            
            <div className={styles.statCard}>
              <span className={styles.statValue}>{demoState.correctCount}/{demoState.totalQuestions}</span>
              <span className={styles.statLabel}>Correct Answers</span>
            </div>
            
            <div className={styles.statCard}>
              <span className={styles.statValue}>{demoState.timeSpent} min</span>
              <span className={styles.statLabel}>Time Spent</span>
            </div>
          </div>

          <div className={styles.actions}>
            <button 
              onClick={handleRetakeDemo} 
              className={styles.retakeButton}
            >
              <img src="/images/icons/version-94.png" alt="retake" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
              Retake Assessment
            </button>
            
            <button 
              onClick={handleExit} 
              className={styles.exitButton}
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render the Questions component with mock data in demo mode
  return (
    <Questions 
      demoMode={true}
      demoModuleVersion={DEMO_MODULE}
      onDemoComplete={calculateDemoScore}
      onDemoExit={handleExit}
    />
  );
};

export default DemoExam;
